// Synthetic demo fleet for the live, backend-free demo build.
//
// Produces fleet data in the exact cloud `/api/nodes` shape (see
// fleet-map/hooks/useNodeFetching.js for the contract) plus the per-node
// payloads the realtime WebSocket enrichment expects
// (fleet-map/hooks/useRealtimeEnrichment.js). All values are a function of a
// controllable simulated clock, so the demo animates live in the browser with
// no backend — and the on-screen controls (see install.js) can scrub the time
// of day or change how fast time runs.
//
// The simulated "day" loops every DAY_MS of sim-time. Solar ramps up midday and
// charges the batteries, load climbs into an evening peak, the batteries
// discharge, and a genset covers the peak — then everything recharges overnight.
//
// A second event, the HeadTrip layout, holds the units of a real event at
// their real relative positions with the readings one snapshot recorded, so
// the browser check judges chip density on a real site (see HEADTRIP_ROWS).
// Both events ride one /api/nodes answer and one WebSocket snapshot. The app
// scopes the map to the selected event on its own, the way it does on the
// cloud.

export const DAY_MS = 4 * 60 * 1000; // sim-ms per full day at speed 1×

// ── controllable simulated clock ──────────────────────────────────────────
// demoNowMs() is a pure function of Date.now() given the current anchors, so it
// can be called from many places (the /api/nodes shim, the WS tick, the control
// panel) without double-counting. No module-level timer runs.
let _speed = 2;          // time multiplier (0 = paused)
let _paused = false;
let _baseRealMs = Date.now();
let _baseSimMs = (10.5 / 24) * DAY_MS; // open mid-morning (solar ramping, batteries charging)

export function demoNowMs() {
  if (_paused) return _baseSimMs;
  return _baseSimMs + (Date.now() - _baseRealMs) * _speed;
}
export function setSpeed(mult) {
  _baseSimMs = demoNowMs();   // re-anchor so time stays continuous across a speed change
  _baseRealMs = Date.now();
  _speed = Math.max(0, mult);
  _paused = _speed === 0;
}
export function setSimHour(h) {
  const hh = (((h % 24) + 24) % 24);
  _baseSimMs = (hh / 24) * DAY_MS;
  _baseRealMs = Date.now();
}
export function getSimHour() {
  return ((demoNowMs() % DAY_MS) / DAY_MS) * 24;
}
export function getSpeed() { return _speed; }
export function isPaused() { return _paused; }

const EVENT_ID = 'demo-event-1';
const EVENT_NAME = 'Live Demo Event';
const DEPLOYMENT_ID = 'demo-fleet';
const HEADTRIP_EVENT_ID = 'demo-event-headtrip';
const HEADTRIP_EVENT_NAME = 'HeadTrip Layout (demo)';

// ── deterministic helpers ────────────────────────────────────────────────
function hash01(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}
const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));
const gauss = (x, mu, s) => Math.exp(-Math.pow((x - mu) / s, 2));
const round = (x, n = 0) => { const p = 10 ** n; return Math.round(x * p) / p; };

function wiggle(now, seed) {
  return 1 + 0.018 * Math.sin(now / 3100 + seed * 6.3) + 0.009 * Math.sin(now / 870 + seed * 11.1);
}
function simHour(now) {
  return ((now % DAY_MS) / DAY_MS) * 24;
}
function solarFactor(h) {
  return Math.max(0, Math.sin((Math.PI * (h - 6.5)) / 11));
}
function loadFactor(h) {
  const evening = Math.max(gauss(h, 22, 3.0), gauss(h + 24, 22, 3.0));
  const afternoon = 0.4 * gauss(h, 16.5, 4.2);
  return clamp(0.12 + 0.9 * evening + afternoon, 0.1, 1);
}
function socBase(h) {
  return 60 + 30 * Math.cos((2 * Math.PI * (h - 15)) / 24); // 30..90
}
function gensetRunning(h) {
  return loadFactor(h) > 0.7 && socBase(h) < 50;
}
function phases(total, now, seed) {
  const a = total * (0.34 + 0.01 * Math.sin(now / 2600 + seed));
  const b = total * (0.33 + 0.01 * Math.sin(now / 2900 + seed + 2));
  const c = total - a - b;
  return [a, b, c];
}

// ── fleet topology (generic event site; open ground, not a named venue) ───
const NODES = [
  { id: 'node-main', name: 'Main Stage', lat: 33.72205, lng: -116.22555,
    systems: [
      { sid: 'pro6010-main-1', type: 'gx', invKw: 30, pvKw: 22, loadKw: 26, loc: '1. Main Stage', capKwh: 10, ratingA: 133 },
      { sid: 'acuvim-main-1', type: 'acuvim', loadKw: 240, loc: '1. Main Stage FOH' },
    ] },
  { id: 'node-east', name: 'East Stage', lat: 33.71985, lng: -116.22305,
    systems: [
      { sid: 'pro505-east-1', type: 'gx', invKw: 15, pvKw: 10, loadKw: 14, loc: '2A East Stage', capKwh: 30.7, ratingA: 33, singlePhase: true },
      // Shares pro505-east-1's DC bank (see DEMO_BANK). A 20 A pro3x run past its
      // rating at the evening peak, so the load column shows amber and red.
      { sid: 'pro3x-east-2', type: 'gx', invKw: 2.4, pvKw: 0, loadKw: 2.6, loc: '2A East Bar', capKwh: 0, ratingA: 20, singlePhase: true },
      { sid: 'acuvim-east-1', type: 'acuvim', loadKw: 180, loc: '2A East Stage' },
    ] },
  { id: 'node-amphi', name: 'Amphitheatre', lat: 33.72155, lng: -116.22765,
    systems: [
      // chargeW: a live EV charge-port reading, so the demo shows the
      // Charging line on one Moxion. The others carry no field at all.
      { sid: 'moxion-amphi-1', type: 'moxion', invKw: 75, loadKw: 60, chargeW: 5134, loc: '3. Amphitheatre', capKwh: 600, ratingA: 100 },
      { sid: 'acuvim-amphi-1', type: 'acuvim', loadKw: 150, loc: '3. Amphitheatre' },
    ] },
  { id: 'node-west', name: 'West Field', lat: 33.71895, lng: -116.22625,
    systems: [
      { sid: 'fronius-solar-1', type: 'fronius', pvKw: 50, loc: '10. West Field Array' },
      { sid: 'pro4010-west-1', type: 'gx', invKw: 20, pvKw: 14, loadKw: 16, loc: '10. West Field', capKwh: 10, ratingA: 133 },
    ] },
  { id: 'node-yard', name: 'Generator Yard', lat: 33.71775, lng: -116.22185,
    systems: [
      // chargerPortW: a running charger-port charge (plan 2026-10-16-1834
      // AE1), so the card reads Charger 58.8 kW.
      { sid: 'leyden-yard-1', type: 'leyden', invKw: 100, loadKw: 80, chargerPortW: 58800, loc: 'Generator Yard', capKwh: 215 },
      { sid: 'dse-genset-1', type: 'dse', genKw: 120, loc: 'Generator Yard' },
      { sid: 'deif-b079', type: 'deif', genKw: 264, loc: 'Generator Yard' },
    ] },
  { id: 'node-vendor', name: 'Vendor Row', lat: 33.72325, lng: -116.22355,
    systems: [
      { sid: 'pro515-vendor-1', type: 'gx', invKw: 15, pvKw: 8, loadKw: 13, loc: '5. Vendor Row North', capKwh: 15, ratingA: 33, singlePhase: true },
      { sid: 'acuvim-vendor-1', type: 'acuvim', loadKw: 120, loc: '5. Vendor Row' },
    ] },
  // Chip states for the mobile map browser check (plan 2026-10-09-1408, U8,
  // KTD17). buildSystem reads the keys: acInW is a constant AC input in W,
  // dcW a constant stored dc_system_power (negative means an external
  // charger, the chip flips the sign), staleS puts data_updated_at that many
  // seconds in the past, alerts is the unit's device alert list, lat and lng
  // give the unit its own coordinates, and bus is its AC bus group and role.
  { id: 'node-office', name: 'Site Office', lat: 33.72095, lng: -116.22455,
    systems: [
      // AC input at 15.5 kW and no PV (AE1). Shares the node point with the
      // stale unit below: a same-point pair with no bus group (AE10).
      { sid: 'pro4005-office-1', type: 'gx', invKw: 15, pvKw: 0, loadKw: 4.6, loc: '6. Site Office', capKwh: 10, ratingA: 133, acInW: 15500 },
      // Stale for ten minutes, with 15.5 kW of AC input last reported (AE12).
      { sid: 'pro505-office-2', type: 'gx', invKw: 15, pvKw: 0, loadKw: 3, loc: '6. Office Trailer', capKwh: 15, ratingA: 33, singlePhase: true, acInW: 15500, staleS: 600 },
      // Own coordinates. An external charger feeds the battery at 3.8 kW
      // (AE3, the chip shows +3.8) and a warning-level alert (amber badge).
      { sid: 'pro505-office-3', type: 'gx', invKw: 15, pvKw: 0, loadKw: 2.5, loc: '6A Office Charger', capKwh: 15, ratingA: 33, singlePhase: true, dcW: -3825, lat: 33.72125, lng: -116.22415, alerts: [{ name: 'hightemperature', level: 1 }] },
      // Own coordinates. DC loads draw 80 W (AE3, the chip shows -0.1) and
      // an alarm-level alert (red badge, AE5).
      { sid: 'pro3x-office-4', type: 'gx', invKw: 2.4, pvKw: 0, loadKw: 1.2, loc: '6B Guard Shack', capKwh: 5, ratingA: 20, singlePhase: true, dcW: 80, lat: 33.72065, lng: -116.22505, alerts: [{ name: 'lowbattery', level: 2 }] },
    ] },
  // A Lot 6A style AC bus group on one point (AE9): the Moxion is the storage
  // member and the Fronius the PV source, neither with its own coordinates,
  // so the map draws one group chip for the pair.
  { id: 'node-lot6a', name: 'Lot 6A', lat: 33.72385, lng: -116.22655,
    systems: [
      { sid: 'moxion-lot6a-1', type: 'moxion', invKw: 75, loadKw: 45, loc: 'Lot 6A', capKwh: 600, ratingA: 100, bus: { group: 'Lot 6A', role: 'storage' } },
      { sid: 'fronius-lot6a-1', type: 'fronius', pvKw: 30, loc: 'Lot 6A Array', bus: { group: 'Lot 6A', role: 'source' } },
    ] },
  // A Navy Cube charged by AC-coupled PV (plan 2026-10-15-1235 AE1): node-4's
  // 2026-10-15 18:55:47Z readings. The Cube's signed port power is -7.7 kW,
  // so the header reads Load 26.29 kW, the battery-side estimate 23.30 kW,
  // Offset 129% and Surplus 7.70 kW, and the Cube card reads 7.70 kW into
  // Cube. Static snapshot rows with cloud field names. The estimate reads
  // PCS DC power (todos/558), set here to the snapshot's battery V x I.
  // PCS DC equal to V x I leaves no charger-port power, so the charger
  // reads 0 W and the card shows Charger 0 kW idle (plan 2026-10-16-1834).
  { id: 'node-stocube', name: 'STO Cube', lat: 33.72455, lng: -116.22535,
    systems: [
      { sid: 'cube-sto-demo', type: 'cube', source: 'edge', loc: 'Stage/Production', bus: { group: 'STO Cube', role: 'storage' },
        reading: { soc: 96, voltage: 737.5, battery_current: -14.5, dc_system_power: -10693.75, charger_port_power: 0, ems_power: -7700, grid_power: 0, pout: -7700,
          va: 470.1, vb: 469.6, vc: 473.9, ia: -14.3, ib: -16.7, ic: -7.0, load_pf: -0.76, load_frequency: 60 } },
      { sid: 'fronius-sto-demo-1', type: 'fronius', source: 'edge', loc: 'Stage/Production PV 1', bus: { group: 'STO Cube', role: 'source' },
        reading: { pout: 19250, pv_power: 19880 } },
      { sid: 'fronius-sto-demo-2', type: 'fronius', source: 'edge', loc: 'Stage/Production PV 2', bus: { group: 'STO Cube', role: 'source' },
        reading: { pout: 14740, pv_power: 15170 } },
    ] },
  // A portal-fed Navy Cube with no node (plan 2026-10-16-1834 AE2): the
  // registry's `portal-<sid>` virtual node and the portal writer's columns.
  // pin and charger_port_power both carry the EMS charger total, so the
  // card reads Charger 58.6 kW and no In reading. Demo values.
  { id: 'portal-cube-portal-demo', name: 'cube-portal-demo', source: 'portal', lat: 33.72265, lng: -116.22185,
    systems: [
      { sid: 'cube-portal-demo', type: 'cube', source: 'portal', loc: 'Backstage Compound',
        reading: { soc: 71, voltage: 768.2, pout: -55400, pin: 58600, input_power_kw: 58.6,
          output_active_power_kw: 3.2, nominal_capacity_kwh: 215, charger_port_power: 58600 } },
    ] },
  // Two Navy Tanks on one point. The first carries a warn verdict (amber
  // badge, AE5). The second carries an unknown verdict (no badge) and holds
  // one reading, which is the condition that verdict describes.
  { id: 'node-tankyard', name: 'Tank Yard', lat: 33.71695, lng: -116.22425,
    systems: [
      { sid: 'tank-yard-1', type: 'tank', loadKw: 25, loc: 'Tank Yard 1', capKwh: 215, tankHealth: 'warn' },
      { sid: 'tank-yard-2', type: 'tank', loadKw: 8, chargerKw: 30, loc: 'Tank Yard 2', capKwh: 215, tankHealth: 'unknown', frozen: true },
    ] },
];

// ── HeadTrip layout (plan 2026-10-09-1408, U8, KTD17) ────────────────────
// A second event that lays out the units of GDV_2026-09_HeadTrip at their
// real relative positions, so the browser check judges chip density on a
// real site. The rows are that event's open memberships joined to
// system_registry, read on the VPS 2026-10-11 00:30 UTC, kept only where the
// unit carried a coordinate of its own. The readings are the snapshot's and
// do not animate. An empty field is null, the way the registry serves it.
// The site spans about 1.57 km east to west and 2.32 km north to south
// around latitude 33.68. The snapshot predates charger_port_power, so the
// Cube card here shows the unknown Charger reading, a dash.
// Columns: system_id|type|source|node_id|lat|lon|soc|pout_w|pin_w|pv_power_w|dc_system_power_w|location
const HEADTRIP_ROWS = `
cube-cn2442-011|cube|edge|node-6|33.688066|-116.237735|44|14903|14900||17000|3A Heavy Equipment
electrolit-crane-shop-db50-2|gx|vrm|vrm|33.678156|-116.234118|93|||0|11|51 & Monroe
phoenix48-1|gx|vrm|vrm|33.671424|-116.250951|98|||0||52 & Madison
phoenix48-8-3g-ecan|gx|vrm|vrm|33.671268|-116.234147|96|||0|27|52 & Monroe
phoenix5-lot16-ecan|gx|vrm|vrm|33.671156|-116.240032|99|||0|37|52
phx48-8|gx|vrm|vrm|33.685483|-116.234343|94|||0|-1|50 & Monroe
pro3x-1|gx|vrm|vrm|33.674091|-116.248848|100|0|0|5.5|5.5|13A OD32
pro3x-10|gx|vrm|vrm|33.674210|-116.241699|99|0|0|0||15A OD37
pro3x-11|gx|vrm|vrm|33.688450|-116.240021|100|0|0|0||2C OD14
pro3x-12|gx|vrm|vrm|33.686584|-116.248550|59|0|0|0||2A OD25
pro3x-16|gx|vrm|vrm|33.673141|-116.242851|100|0|0|5.5||14C OD36
pro3x-18|gx|vrm|vrm|33.691978|-116.238945|100|0|0|0||1B OD01
pro3x-19|gx|vrm|vrm|33.689491|-116.236259|100|0|0|0||1C OD4
pro3x-2|gx|vrm|vrm|33.691982|-116.236816|95|0|0|0|0|1B OD2
pro3x-25|gx|vrm|vrm|33.671966|-116.235405|100|0|0|0|0|16 OD40
pro3x-26|gx|vrm|vrm|33.683477|-116.246027|85|772|0|0||5A OD22
pro3x-27|gx|vrm|vrm|33.674569|-116.244723|99|4|0|5.4|5.4|10B OD35
pro3x-28|gx|vrm|vrm|33.674872|-116.247244|100|0|0|0||10A OD31
pro3x-29|gx|vrm|vrm|33.687735|-116.234187|93|597|953|||3F OD15
pro3x-3|gx|vrm|vrm|33.689922|-116.237274|100|0|0|0||1C OD5
pro3x-4|gx|vrm|vrm|33.683964|-116.242744|80|646|0|0|61|5B Yellow Path Box Office
pro3x-5|gx|vrm|vrm|33.687683|-116.240898|100|0|0|0||2C OD12
pro3x-51|gx|vrm|vrm|33.673301|-116.240174|100|-6|29|||15C OD38
pro3x-6|gx|vrm|vrm|33.671730|-116.238724|100|0|0|0|0|15D OD39
pro3x-7|gx|vrm|vrm|33.683006|-116.249954|98|79|0|0||4B OD24
pro3x-8|gx|vrm|vrm|33.689838|-116.239044|100|2|35|0||1C OD6
pro3x-9|gx|vrm|vrm|33.689827|-116.241035|100|4|0|0||1A OD7
pro3x-nanuk|gx|vrm|vrm|33.679532|-116.243699|86|4|0|||8A OD28
pro4005|gx|vrm|vrm|33.681786|-116.242744|68|5213|17626|||8V Bearcom
pro505-3|gx|vrm|vrm|33.683788|-116.243698|97|0|0|0|0|5B - OD19
pro505-4|gx|vrm|vrm|33.679955|-116.251060|73|1567|0|0|-2333|7A OD26
pro505-5|gx|vrm|vrm|33.678482|-116.245636|51|331|0|0|-285|8A OD29
pro505-6|gx|vrm|vrm|33.680214|-116.242531|63|1808|0|0|-641|7V ABC Office
pro515-2|gx|vrm|vrm|33.677982|-116.245361|33|1311|0|0|-755|10A OD30 + Office
pro515-3|gx|vrm|vrm|33.672413|-116.234116|70|149|0|0||16A Resourses Check-in n
pro6010-1|gx|vrm|vrm|33.676662|-116.238946|100|3550|4019|||11B Sign Shop
`;

const numOrNull = (s) => (s === '' ? null : Number(s));

const HEADTRIP_NODES = HEADTRIP_ROWS.trim().split('\n').map((line) => {
  const [sid, type, source, nodeId, lat, lon, soc, pout, pin, pv, dc, loc] = line.split('|');
  const isVrm = source === 'vrm';
  return {
    // A VRM unit gets its own virtual node, the cloud registry's
    // vrm-<system_id> shell. The edge node keeps its real id behind a prefix
    // that cannot collide with the demo site's node ids.
    id: isVrm ? `vrm-${sid}` : `ht-${nodeId}`,
    name: isVrm ? sid : nodeId,
    source,
    eventId: HEADTRIP_EVENT_ID,
    lat: Number(lat),
    lng: Number(lon),
    systems: [{
      sid, type, loc, source,
      reading: { soc: numOrNull(soc), pout: numOrNull(pout), pin: numOrNull(pin), pv_power: numOrNull(pv), dc_system_power: numOrNull(dc) },
      // The VRM unit is placed by its own fix. The edge unit sits on its
      // node's point, like every edge unit of the demo site. gps_source is a
      // demo value: the snapshot does not carry it.
      ...(isVrm ? { lat: Number(lat), lng: Number(lon), gpsSource: 'modem' } : {}),
    }],
  };
});

const ALL_NODES = [...NODES, ...HEADTRIP_NODES];
const nodesForEvent = (eventId) => (eventId === HEADTRIP_EVENT_ID ? HEADTRIP_NODES : NODES);

// ── per-system value builders ─────────────────────────────────────────────
function buildSystem(cfg, now, dataTs, eventId = EVENT_ID) {
  const h = simHour(now);
  const seed = hash01(cfg.sid) * 12;
  const w = wiggle(now, seed);
  const solar = solarFactor(h);
  const load = loadFactor(h);
  const alerts = Array.isArray(cfg.alerts) ? cfg.alerts : [];
  const specs = {
    ...(cfg.ratingA ? { spec_current_a: String(cfg.ratingA) } : {}),
    ...(cfg.capKwh != null ? { battery_capacity_kwh: String(cfg.capKwh) } : {}),
    ...(cfg.bus ? { ac_bus_group: cfg.bus.group, ac_bus_role: cfg.bus.role } : {}),
  };
  const base = {
    system_id: cfg.sid,
    portal_id: cfg.sid,
    type: cfg.type,
    // A stale unit keeps its last readings behind an old timestamp (AE12).
    data_updated_at: cfg.staleS ? dataTs - cfg.staleS : dataTs,
    stale: !!cfg.staleS,
    online: true,
    event_id: eventId,
    // The drawer's operator sections (branch links, shared banks) read the
    // membership's window through this field.
    window_id: eventId,
    alerts,
    alerts_count: alerts.filter((a) => a.level >= 2).length,
    ...(cfg.loc ? { location: cfg.loc } : {}),
    // A unit with coordinates of its own sits at them, not at the node point.
    ...(cfg.lat != null ? { latitude: cfg.lat, longitude: cfg.lng, gps_source: cfg.gpsSource || 'gx' } : {}),
    ...(Object.keys(specs).length ? { equipment: { specs, load: {} } } : {}),
  };

  // A HeadTrip layout unit carries the readings the snapshot recorded.
  if (cfg.reading) {
    return { ...base, portal_id: null, source: cfg.source, ...cfg.reading };
  }

  if (cfg.type === 'gx') {
    const soc = clamp(socBase(h) + (seed - 6) * 1.2, 6, 99);
    const pv = solar * cfg.pvKw * 1000 * w;
    const demand = load * cfg.loadKw * 1000 * w;
    const net = demand - pv;
    const pout = net > 0 ? net : 0;
    const pin = net < 0 ? net : 0;
    // Single-phase units carry only L1, the way a pro505 or pro3x reports.
    const [l1, l2, l3] = cfg.singlePhase ? [pout, null, null] : phases(pout, now, seed);
    const amps = (w) => (w == null ? null : Math.round((w / 120) * 10) / 10);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      voltage: Math.round((50.5 + soc * 0.035 + 0.2 * Math.sin(now / 4000 + seed)) * 10) / 10,
      ia: amps(l1), ib: amps(l2), ic: amps(l3),
      battery_temperature: Math.round((24 + load * 9 + Math.sin(now / 5200 + seed)) * 10) / 10,
      pout: Math.round(pout),
      pin: cfg.acInW != null ? cfg.acInW : Math.round(pin),
      pv_power: Math.round(pv),
      ac_out_power: Math.round(pout),
      dc_system_power: cfg.dcW != null ? cfg.dcW : Math.round((pv - pout) * 0.04),
      mode: net > 0 ? 'Inverter' : pv > demand ? 'Charger' : 'PassThru',
      ac_out_l1: Math.round(l1), ac_out_l2: l2 == null ? null : Math.round(l2), ac_out_l3: l3 == null ? null : Math.round(l3),
      temperature_sensors: { 0: { temperature: Math.round(28 + load * 16 + 2 * Math.sin(now / 5000 + seed)), type: 0 } },
      relays: { 0: false, 1: pout > cfg.invKw * 400 },
      pv_chargers: {},
    };
  }

  if (cfg.type === 'acuvim') {
    const p = load * cfg.loadKw * 1000 * w;
    const v = 277 + 1.5 * Math.sin(now / 3500 + seed);
    const [i1, i2, i3] = phases(p / v, now, seed);
    return {
      ...base,
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.6) * 10) / 10, vc: Math.round((v + 0.5) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      vavg: Math.round(v * 10) / 10,
      iavg: Math.round(((i1 + i2 + i3) / 3) * 10) / 10,
      p: Math.round(p),
    };
  }

  if (cfg.type === 'fronius') {
    const ac = solar * cfg.pvKw * 1000 * w;
    const v = 277 + 1.2 * Math.sin(now / 3300 + seed);
    const [i1, i2, i3] = phases(ac / v, now, seed);
    return {
      ...base,
      pout: Math.round(ac),
      pv_power: Math.round(ac),
      va: Math.round(v * 10) / 10, vb: Math.round((v + 0.4) * 10) / 10, vc: Math.round((v - 0.5) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      dc_voltage: Math.round((585 + 8 * solar) * 10) / 10,
      dc_current: Math.round((ac / 585) * 10) / 10,
      energy_total_wh: Math.round(2_450_000 + ((now / 1000) % 86400) * 0.6),
    };
  }

  if (cfg.type === 'moxion') {
    const soc = clamp(socBase(h) + (seed - 6) * 1.0, 8, 98);
    const demand = load * cfg.loadKw * 1000 * w;
    const dc = demand * 1.04;
    const [l1, l2, l3] = phases(demand, now, seed);
    const v = 277 + 1.3 * Math.sin(now / 3700 + seed);
    const [i1, i2, i3] = phases(demand / v, now, seed);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      bms_soc: Math.round(soc * 10) / 10,
      voltage: Math.round((720 + soc * 0.4) * 10) / 10,
      power: Math.round(dc),
      pcs_power: Math.round(demand),
      ac_out_l1: Math.round(l1), ac_out_l2: Math.round(l2), ac_out_l3: Math.round(l3),
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.5) * 10) / 10, vc: Math.round((v + 0.4) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      ...(cfg.chargeW != null ? { charge_power: cfg.chargeW } : {}),
    };
  }

  if (cfg.type === 'dse') {
    const running = gensetRunning(h);
    const out = running ? cfg.genKw * 1000 * (0.55 + 0.35 * load) * w : 0;
    const v = running ? 277 + 1.0 * Math.sin(now / 2500 + seed) : 0;
    const [i1, i2, i3] = running ? phases(out / v, now, seed) : [0, 0, 0];
    return {
      ...base,
      pout: Math.round(out),
      power: Math.round(out),
      engine_rpm: running ? Math.round(1800 + 6 * Math.sin(now / 1500 + seed)) : 0,
      gen_frequency: running ? Math.round((60 + 0.08 * Math.sin(now / 1200 + seed)) * 100) / 100 : 0,
      coolant_temp: running ? Math.round(83 + 3 * Math.sin(now / 6000 + seed)) : 41,
      oil_pressure: running ? Math.round(345 + 8 * Math.sin(now / 4000 + seed)) : 0,
      voltage_l1: Math.round(v * 10) / 10, voltage_l2: Math.round((v - 0.5) * 10) / 10, voltage_l3: Math.round((v + 0.4) * 10) / 10,
      current_l1: Math.round(i1 * 10) / 10, current_l2: Math.round(i2 * 10) / 10, current_l3: Math.round(i3 * 10) / 10,
    };
  }

  if (cfg.type === 'deif') {
    // Numbers track a real AGC-4 (deif-b079, node-7): 264 kW nameplate, 1800 rpm,
    // imperial units (deg F / psi / gal/h), ~15 kWh/gal at half load.
    const running = gensetRunning(h);
    const kw = running ? cfg.genKw * (0.45 + 0.25 * load) * w : 0;
    const v = running ? 485 + 1.5 * Math.sin(now / 2500 + seed) : 0;
    const [i1, i2, i3] = running ? phases((kw * 1000) / (v * 1.732), now, seed) : [0, 0, 0];
    const gph = running ? kw / 15.2 : 0;
    return {
      ...base,
      pout: Math.round(kw),
      power: Math.round(kw),
      engine_rpm: running ? Math.round(1800 + 4 * Math.sin(now / 1500 + seed)) : 0,
      gen_frequency: running ? Math.round((60 + 0.06 * Math.sin(now / 1200 + seed)) * 100) / 100 : 0,
      coolant_temp: running ? Math.round(186 + 3 * Math.sin(now / 6000 + seed)) : 78,
      oil_pressure: running ? Math.round(52 + 2 * Math.sin(now / 4000 + seed)) : 0,
      fuel_rate: Math.round(gph * 10) / 10,
      fuel_efficiency: running ? Math.round((kw / gph) * 10) / 10 : null,
      percent_load: running ? Math.round((kw / cfg.genKw) * 100) : 0,
      excitation_current: running ? Math.round(13 + 1 * Math.sin(now / 3000 + seed)) : 0,
      // Latched by the edge once the engine has run warm; stays set while
      // the genset is stopped, which is the whole point of latching it.
      units_imperial: true,
      run_hours: 8327,
      alarm_count: 2,
      nominal_power: cfg.genKw,
      voltage_l1: Math.round(v * 10) / 10,
      voltage_l2: Math.round((v + 1) * 10) / 10,
      voltage_l3: Math.round(v * 10) / 10,
      current_l1: Math.round(i1 * 10) / 10,
      current_l2: Math.round(i2 * 10) / 10,
      current_l3: Math.round(i3 * 10) / 10,
    };
  }

  if (cfg.type === 'leyden') {
    const soc = clamp(socBase(h) + (seed - 6) * 0.9, 8, 98);
    const demand = load * cfg.loadKw * 1000 * w;
    const dcv = 812 + soc * 0.5;
    const v = 277 + 1.1 * Math.sin(now / 3100 + seed);
    const [i1, i2, i3] = phases(demand / v, now, seed);
    const [gi1, gi2, gi3] = phases((demand * 0.2) / v, now, seed + 3);
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      dc_voltage: Math.round(dcv * 10) / 10,
      battery_current: Math.round((demand / dcv) * 10) / 10,
      pout: Math.round(demand),
      dc_power: Math.round(demand * 1.03),
      ems_power: Math.round(demand),
      grid_power: Math.round(-demand * 0.2),
      grid_frequency: Math.round((60 + 0.05 * Math.sin(now / 1800 + seed)) * 100) / 100,
      load_frequency: Math.round((60 + 0.04 * Math.sin(now / 2000 + seed)) * 100) / 100,
      load_pf: Math.round((0.95 + 0.02 * Math.sin(now / 5000 + seed)) * 100) / 100,
      cell_vmin: Math.round((3.21 + soc * 0.0012) * 1000) / 1000,
      cell_vmax: Math.round((3.34 + soc * 0.0009) * 1000) / 1000,
      va: Math.round(v * 10) / 10, vb: Math.round((v - 0.5) * 10) / 10, vc: Math.round((v + 0.4) * 10) / 10,
      ia: Math.round(i1 * 10) / 10, ib: Math.round(i2 * 10) / 10, ic: Math.round(i3 * 10) / 10,
      grid_va: Math.round(v * 10) / 10, grid_vb: Math.round((v - 0.4) * 10) / 10, grid_vc: Math.round((v + 0.3) * 10) / 10,
      grid_ia: Math.round(gi1 * 10) / 10, grid_ib: Math.round(gi2 * 10) / 10, grid_ic: Math.round(gi3 * 10) / 10,
      ...(cfg.chargerPortW != null ? { charger_port_power: cfg.chargerPortW } : {}),
    };
  }

  if (cfg.type === 'tank') {
    // Navy Tank, under the cloud registry's tank branch names (registry.py):
    // pout is signed net battery power (plus is discharge), pin the charger
    // module input, output_active_power_kw the AC output the tank serves.
    // The verdict scalars ride the edge realtime payload (tank_health.py).
    // A frozen tank holds one reading at one hour of the day, which is the
    // condition the unknown verdict and its "data frozen" top issue name.
    const fh = cfg.frozen ? 9 : h;
    const fw = cfg.frozen ? 1 : w;
    const soc = clamp(socBase(fh) + (seed - 6) * 0.9, 8, 98);
    const outW = loadFactor(fh) * cfg.loadKw * 1000 * fw;
    const chargerW = (cfg.chargerKw || 0) * 1000 * fw;
    const verdict = cfg.tankHealth || 'ok';
    const rank = { critical: 4, warn: 3, unknown: 2, limited: 0, ok: 0 }[verdict] ?? 2;
    const topIssue = verdict === 'warn' ? 'cell imbalance' : verdict === 'unknown' ? 'data frozen' : null;
    return {
      ...base,
      soc: Math.round(soc * 10) / 10,
      voltage: Math.round((51.2 + soc * 0.03) * 10) / 10,
      pout: Math.round(outW * 1.08 - chargerW),
      pin: Math.round(chargerW),
      input_power_kw: round(chargerW / 1000, 2),
      output_active_power_kw: round(outW / 1000, 2),
      nominal_capacity_kwh: cfg.capKwh,
      tank_health: verdict,
      tank_health_rank: rank,
      tank_top_issue: topIssue,
      tank_issue_count: topIssue ? 1 : 0,
    };
  }

  return base;
}

function buildNode(cfg, now) {
  // Real wall-clock seconds, not simulated time: freshness compares data
  // time with Date.now(), and sim time (minutes past 1970) made every demo
  // row read offline.
  const dataTs = Math.floor(Date.now() / 1000);
  const eventId = cfg.eventId || EVENT_ID;
  const systems = cfg.systems.map((s) => buildSystem(s, now, dataTs, eventId));
  // The types the cloud registry promotes to the node header (registry.py
  // _build_nodes_from_registry).
  const battery = systems.find((s) => ['gx', 'moxion', 'leyden', 'cube', 'tank'].includes(s.type)) || systems[0];
  const acuvim = systems.find((s) => s.type === 'acuvim');
  // Node-level alerts are the alarm-level device alerts across its systems,
  // the way the cloud card recomputes them from a realtime payload.
  const alerts = systems.flatMap((s) => (s.alerts || []).filter((a) => a.level >= 2).map((a) => a.name));
  const node = {
    node_id: cfg.id,
    system_id: battery?.system_id || cfg.id,
    host_system_id: battery?.system_id || null,
    deployment_id: DEPLOYMENT_ID,
    event_id: eventId,
    event_updated_at: dataTs,
    source: 'edge',
    version: 'v1.3.0',
    addresses: [],
    node_url: null,
    online: true,
    systems,
    soc: battery?.soc ?? null,
    voltage: battery?.voltage ?? null,
    pin: battery?.pin ?? null,
    pout: battery?.pout ?? battery?.pcs_power ?? null,
    pv_power: battery?.pv_power ?? null,
    mode: battery?.mode ?? null,
    location: cfg.name,
    latitude: cfg.lat,
    longitude: cfg.lng,
    gps_source: 'manual',
    last_seen_at: dataTs,
    data_updated_at: dataTs,
    alerts,
    alerts_count: alerts.length,
    acuvim_vavg: acuvim?.vavg ?? null,
    acuvim_iavg: acuvim?.iavg ?? null,
    acuvim_p: acuvim?.p ?? null,
    acuvim_updated_at: acuvim ? dataTs : null,
    node_health: null,
  };
  if (cfg.source === 'vrm' || cfg.source === 'portal') {
    // The cloud registry's virtual node shell (registry.py _build_node_shell):
    // a VRM or portal writer plus the unit it wrote about, no machine behind
    // it, no version, no presence, and the unit's own id as its name. A VRM
    // site belongs to no deployment; a portal row keeps its deployment_id.
    const unit = systems[0];
    Object.assign(node, {
      name: unit.system_id,
      host_system_id: null,
      deployment_id: cfg.source === 'portal' ? DEPLOYMENT_ID : null,
      source: cfg.source,
      version: null,
      manual: null,
      state: 'active',
      location: unit.location ?? null,
      gps_source: unit.gps_source ?? null,
      last_seen_at: null,
    });
  }
  return node;
}

// ── public API used by the install shim ───────────────────────────────────
export function computeFleet(nowMs) {
  const now = nowMs == null ? demoNowMs() : nowMs;
  return ALL_NODES.map((cfg) => buildNode(cfg, now));
}

export function computeWsSnapshot(nowMs) {
  const now = nowMs == null ? demoNowMs() : nowMs;
  const nodes = {};
  for (const node of ALL_NODES.map((cfg) => buildNode(cfg, now))) {
    nodes[node.node_id] = {
      node_id: node.node_id,
      event_id: node.event_id,
      ts: Date.now(),
      version: node.version,
      last_seen_at: node.last_seen_at,
      systems: node.systems,
    };
  }
  return { type: 'snapshot', nodes };
}

// Smooth (no-wiggle) power decomposition for energy integration.
function systemPowers(cfg, h) {
  const solar = solarFactor(h);
  const load = loadFactor(h);
  if (cfg.reading) {
    // A snapshot unit delivers its recorded AC output and PV, held flat.
    const out = Math.max(0, cfg.reading.pout || 0);
    return { deliver_w: out, pv_w: cfg.reading.pv_power || 0, gen_w: 0, load_w: out };
  }
  if (cfg.type === 'gx') {
    const pv = solar * cfg.pvKw * 1000;
    const demand = load * cfg.loadKw * 1000;
    return { deliver_w: Math.max(0, demand - pv), pv_w: pv, gen_w: 0, load_w: demand };
  }
  if (cfg.type === 'acuvim') {
    const p = load * cfg.loadKw * 1000;
    return { deliver_w: p, pv_w: 0, gen_w: 0, load_w: p };
  }
  if (cfg.type === 'fronius') {
    const pv = solar * cfg.pvKw * 1000;
    return { deliver_w: pv, pv_w: pv, gen_w: 0, load_w: 0 };
  }
  if (cfg.type === 'moxion' || cfg.type === 'leyden' || cfg.type === 'tank') {
    const demand = load * cfg.loadKw * 1000;
    return { deliver_w: demand, pv_w: 0, gen_w: 0, load_w: demand };
  }
  if (cfg.type === 'dse') {
    const g = gensetRunning(h) ? cfg.genKw * 1000 * (0.55 + 0.35 * load) : 0;
    return { deliver_w: g, pv_w: 0, gen_w: g, load_w: 0 };
  }
  return { deliver_w: 0, pv_w: 0, gen_w: 0, load_w: 0 };
}

// Synthetic `/api/reports/live/<event>` — per-system accumulated energy + an
// event-level by-source split (solar / generator / battery), integrated from
// the start of the simulated day to the current sim time. Responds to the
// time/speed controls (scrub forward → more accumulated energy).
const FUEL_KWH_PER_GAL = 13.5; // diesel genset, typical
const DIESEL_PRICE = 4.25;
const CO2_KG_PER_GAL = 10.18;

export function computeLiveReport(now = demoNowMs(), eventId = EVENT_ID) {
  const fleet = nodesForEvent(eventId);
  const hNow = simHour(now);
  const STEP = 0.05; // hours
  const sysAccum = {};
  for (const node of fleet) for (const s of node.systems) sysAccum[s.sid] = { energy_wh: 0, pv_energy_wh: 0 };

  let totalLoadWh = 0, pvServedWh = 0, genServedWh = 0, battServedWh = 0, pvGenWh = 0;
  for (let t = 0; t < hNow; t += STEP) {
    let fleetLoadW = 0, fleetPvGenW = 0, fleetGenW = 0;
    for (const node of fleet) for (const s of node.systems) {
      const p = systemPowers(s, t);
      sysAccum[s.sid].energy_wh += p.deliver_w * STEP;
      sysAccum[s.sid].pv_energy_wh += p.pv_w * STEP;
      fleetLoadW += p.load_w;
      fleetPvGenW += p.pv_w;
      fleetGenW += p.gen_w;
    }
    const pvServed = Math.min(fleetLoadW, fleetPvGenW);
    const genServed = Math.min(fleetGenW, Math.max(0, fleetLoadW - pvServed));
    const battServed = Math.max(0, fleetLoadW - pvServed - genServed);
    totalLoadWh += fleetLoadW * STEP;
    pvServedWh += pvServed * STEP;
    genServedWh += genServed * STEP;
    battServedWh += battServed * STEP;
    pvGenWh += fleetPvGenW * STEP;
  }

  const liveSys = {};
  for (const n of computeFleet(now)) for (const s of n.systems) liveSys[s.system_id] = s;

  // Time left to the default 10% reserve, the way the cloud estimate reads:
  // a countdown while the unit delivers, Charging while it takes power in.
  const timeLeft = (live, capKwh, powerW) => {
    if (live.pin != null && live.pin < -50) return { confidence: 'charging', method: 'charging' };
    if (!capKwh || !(powerW > 50) || live.soc == null) return null;
    const remainingWh = Math.max(0, live.soc - DEMO_RESERVE_PCT) / 100 * capKwh * 1000;
    return { confidence: 'high', method: 'hybrid', seconds: Math.round((remainingWh / powerW) * 3600) };
  };

  const systems = {};
  for (const node of fleet) for (const s of node.systems) {
    const acc = sysAccum[s.sid];
    const live = liveSys[s.sid] || {};
    const isGen = s.type === 'dse';
    const isPvSrc = s.type === 'fronius';
    const isBatt = ['gx', 'moxion', 'leyden', 'tank', 'cube'].includes(s.type);
    const isLoad = s.type === 'acuvim';
    // A tank's pout is signed net battery power. Its delivered AC output is
    // output_active_power_kw.
    const curW = isLoad ? (live.p || 0)
      : s.type === 'tank' ? (live.output_active_power_kw || 0) * 1000
        : (live.pout || live.pcs_power || 0);
    const gal = isGen ? 0 : (acc.energy_wh / 1000) / FUEL_KWH_PER_GAL;
    systems[s.sid] = {
      system_id: s.sid,
      system_type: s.type,
      energy_role: isGen ? 'generator' : isPvSrc ? 'pv' : isBatt ? 'bess' : 'load',
      ac_input_source: isGen ? 'generator' : isBatt ? 'battery' : isPvSrc ? 'pv' : null,
      location: s.loc || node.name,
      energy_wh: Math.round(acc.energy_wh),
      current_power_w: Math.round(curW),
      pv_energy_wh: Math.round(acc.pv_energy_wh),
      pv_power_w: Math.round(live.pv_power || (isPvSrc ? live.pout : 0) || 0),
      is_current_location: true,
      period_start: null, period_end: null, joined_at: null,
      unit_token: null, unit_label: s.sid,
      fuel_savings: {
        fuel_saved_gal: round(gal, 1),
        co2_avoided_kg: round(gal * CO2_KG_PER_GAL, 1),
        cost_saved: round(gal * DIESEL_PRICE, 2),
        fuel_type: 'diesel',
      },
      // The demo site's batteries link to its array and genset. The HeadTrip
      // layout carries no links.
      linked_sources: isBatt && eventId === EVENT_ID
        ? { grid: [], pv: ['fronius-solar-1'], gen: ['dse-genset-1'] } : { grid: [], pv: [], gen: [] },
    };
    if (isBatt) {
      const tr = timeLeft(live, s.capKwh, curW);
      Object.assign(systems[s.sid], {
        time_remaining_capacity_kwh: s.capKwh || null,
        time_remaining_reserve_pct: DEMO_RESERVE_PCT,
        ...(tr ? { time_remaining: tr } : {}),
      });
    }
  }

  // The shared bank: one estimate over the pack pro505-east-1's BMS reports,
  // at both inverters' combined output, copied onto both rows.
  const members = DEMO_BANK.members.map((sid) => liveSys[sid]).filter(Boolean);
  const bankPowerW = members.reduce((w, m) => w + (m.pout || 0), 0);
  const bmsUnit = liveSys[DEMO_BANK.bmsMember] || {};
  const bankTr = timeLeft(bmsUnit, DEMO_BANK.capacityKwh, bankPowerW);
  for (const sid of DEMO_BANK.members) {
    if (!systems[sid]) continue;
    systems[sid].bank = {
      bank_id: DEMO_BANK.bank_id, members: [...DEMO_BANK.members].sort(),
      soc: bmsUnit.soc ?? null, capacity_source: 'bms_sum',
    };
    systems[sid].time_remaining_capacity_kwh = DEMO_BANK.capacityKwh;
    delete systems[sid].time_remaining;
    if (bankTr) systems[sid].time_remaining = bankTr;
  }

  const totalGal = (pvServedWh + battServedWh) / 1000 / FUEL_KWH_PER_GAL;
  return {
    event_id: eventId,
    duration_seconds: Math.round(hNow * 3600),
    systems,
    total_energy_wh: Math.round(totalLoadWh),
    total_pv_energy_wh: Math.round(pvGenWh),
    total_grid_energy_wh: 0,
    total_gen_energy_wh: Math.round(genServedWh),
    total_battery_energy_wh: Math.round(battServedWh),
    event_summary: {
      total_delivered_wh: Math.round(totalLoadWh),
      grid_wh: 0,
      pv_wh: Math.round(pvServedWh),
      gen_wh: Math.round(genServedWh),
      direct_to_load_wh: { pv: Math.round(pvServedWh) },
    },
    load_attribution: {
      grid_wh: 0,
      pv_wh: Math.round(pvServedWh),
      gen_wh: Math.round(genServedWh),
      battery_wh: Math.round(battServedWh),
      unassigned_wh: 0,
      battery_reattribution: { grid_wh: 0, pv_wh: Math.round(battServedWh * 0.6), gen_wh: Math.round(battServedWh * 0.4) },
    },
    total_fuel_saved_gal: round(totalGal, 1),
    total_co2_avoided_kg: round(totalGal * CO2_KG_PER_GAL, 1),
    total_cost_saved: round(totalGal * DIESEL_PRICE, 2),
    computed_at: new Date().toISOString(),
    schema_version: 2,
  };
}

const DEMO_RESERVE_PCT = 10;

// One operator-linked shared battery bank (plan 2026-09-26-0019, U4): the
// pro3x on the bar shares the pro505's pack, whose BMS reports it.
export const DEMO_BANK = {
  bank_id: 'demo-bank-1',
  members: ['pro505-east-1', 'pro3x-east-2'],
  bmsMember: 'pro505-east-1',
  capacityKwh: 30.7,
};

/** The /shared-banks list the drawer's link editor reads. Only the demo
 * site's window has a bank. Any other window answers the empty list. */
export function computeSharedBanks(now = demoNowMs(), windowId = EVENT_ID) {
  if (windowId !== EVENT_ID) return { window_id: windowId, ended: false, banks: [], candidates: [] };
  const live = {};
  for (const n of computeFleet(now)) for (const s of n.systems) live[s.system_id] = s;
  const unit = (sid, cfg) => ({
    system_id: sid,
    capacity_kwh: cfg?.capKwh || null,
    capacity_source: cfg?.capKwh ? 'bms' : null,
    bms_fresh: sid === DEMO_BANK.bmsMember,
    bms_soc_at: sid === DEMO_BANK.bmsMember ? new Date().toISOString() : null,
  });
  const cfgOf = {};
  for (const node of NODES) for (const s of node.systems) cfgOf[s.sid] = s;
  const batteries = Object.keys(cfgOf).filter((sid) => ['gx', 'moxion', 'leyden'].includes(cfgOf[sid].type));
  return {
    window_id: EVENT_ID,
    ended: false,
    banks: [{
      bank_id: DEMO_BANK.bank_id,
      window_id: EVENT_ID,
      capacity_override_kwh: null,
      created_by: 'demo',
      members: DEMO_BANK.members.map((sid) => ({ ...unit(sid, cfgOf[sid]), membership_active: true })),
      bms_sum_kwh: DEMO_BANK.capacityKwh,
    }],
    candidates: batteries
      .filter((sid) => !DEMO_BANK.members.includes(sid))
      .map((sid) => ({ ...unit(sid, cfgOf[sid]), system_type: cfgOf[sid].type, bms_fresh: live[sid] != null })),
  };
}

// ── generator runway advisor (plan 2026-10-17-2154, U10) ─────────────────
// The /api/runway read for the demo site, in the shape routes/runway.py
// serves. Times run on the real clock, not the simulated one, because the
// section writes them in venue time. Writes merge into the state below, so
// a save in the drawer reads back. Coverage follows the setup the way the
// advisor does: no load-out or no floor means not covered.
const RUNWAY_TZ = 'America/Los_Angeles';
const QUARTER_MS = 15 * 60 * 1000;

// s0: start SOC. draw: SOC points lost per 15 minutes. gen: the advised run
// as [first step, steps], or null for no generator. stale: telemetry gone.
const RUNWAY_BATTERIES = [
  { sid: 'cube-portal-demo', sysType: 'cube', s0: 71, draw: 1.15, gen: [20, 6], lowPct: 9 },
  { sid: 'cube-sto-demo', sysType: 'cube', s0: 96, draw: 1.0, gen: null, lowPct: 11 },
  { sid: 'leyden-yard-1', sysType: 'leyden', s0: 64, draw: 0.9, gen: null, lowPct: 14 },
  { sid: 'tank-yard-1', sysType: 'tank', s0: 48, draw: 0.8, gen: null, lowPct: 10, stale: true },
];

function venueDay(ms) {
  const p = {};
  for (const x of new Intl.DateTimeFormat('en-US', {
    timeZone: RUNWAY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(ms))) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

// "2026-10-16 (Fri)", the gatherer's copied-day label (runway_inputs._day_label).
const withWeekday = (day) => `${day} (${new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', weekday: 'short',
}).format(new Date(`${day}T00:00:00Z`))})`;

const isoUtc = (ms) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, '+00:00');
const quarterUp = (ms) => Math.ceil(ms / QUARTER_MS) * QUARTER_MS;

const BOOT_MS = quarterUp(Date.now());
const DAY_REAL_MS = 24 * 3600 * 1000;

const runwayState = {
  event: {
    generator_kw: 56,
    show_end_override: null,
    load_out_at: isoUtc(BOOT_MS + 14 * 3600 * 1000),
    show_days: [venueDay(BOOT_MS - DAY_REAL_MS), venueDay(BOOT_MS)],
    expected_show_load_kw: null,
    job_channel_id: 'C07DEMOJOB',
    site_lat: null,
    site_lon: null,
    updated_by: 'demo',
    updated_at: isoUtc(BOOT_MS - 3600 * 1000),
  },
  setups: {
    'cube-portal-demo': {
      generator_kw_override: null,
      autostart_start_soc: 30, autostart_start_soc_at: isoUtc(BOOT_MS - 5 * 3600 * 1000),
      autostart_stop_soc: 80, autostart_stop_soc_at: isoUtc(BOOT_MS - 5 * 3600 * 1000),
      autostart_armed: true, autostart_armed_at: isoUtc(BOOT_MS - 5 * 3600 * 1000),
      charger_source_kind: 'moxion', charger_source_state: 'available', charger_source_remaining_kwh: null,
      floor_soc: 12, floor_margin: 3, pv_kw_override: null,
      updated_by: 'demo', updated_at: isoUtc(BOOT_MS - 5 * 3600 * 1000),
    },
    'cube-sto-demo': {
      generator_kw_override: null,
      autostart_start_soc: 20, autostart_start_soc_at: isoUtc(BOOT_MS - 6 * 3600 * 1000),
      autostart_stop_soc: 70, autostart_stop_soc_at: isoUtc(BOOT_MS - 6 * 3600 * 1000),
      autostart_armed: false, autostart_armed_at: isoUtc(BOOT_MS - 6 * 3600 * 1000),
      charger_source_kind: null, charger_source_state: null, charger_source_remaining_kwh: null,
      floor_soc: 14, floor_margin: 3, pv_kw_override: null,
      updated_by: 'demo', updated_at: isoUtc(BOOT_MS - 6 * 3600 * 1000),
    },
    'tank-yard-1': {
      generator_kw_override: 30,
      autostart_start_soc: null, autostart_start_soc_at: null,
      autostart_stop_soc: null, autostart_stop_soc_at: null,
      autostart_armed: null, autostart_armed_at: null,
      charger_source_kind: 'generator', charger_source_state: 'available', charger_source_remaining_kwh: null,
      floor_soc: 13, floor_margin: 3, pv_kw_override: null,
      updated_by: 'demo', updated_at: isoUtc(BOOT_MS - 8 * 3600 * 1000),
    },
  },
  proposals: [
    {
      id: 9001, system_id: null, field: 'load_out_at', value: isoUtc(BOOT_MS + 13 * 3600 * 1000), unit: null,
      author: 'U02DEMOCREW', excerpt: 'Load out is tomorrow 9am, trucks at the gate by 8:30',
      created_at: isoUtc(BOOT_MS - 40 * 60 * 1000), current_value: null,
    },
    {
      id: 9002, system_id: 'cube-portal-demo', field: 'charger_source_state', value: 'spent', unit: null,
      author: 'U02DEMOTECH', excerpt: 'the Moxion on the backstage Cube died, swapping it in the morning',
      created_at: isoUtc(BOOT_MS - 25 * 60 * 1000), current_value: 'available',
    },
  ],
};

function runwaySteps(cfg, nowMs, endMs) {
  const traj = [];
  const base = [];
  let soc = cfg.s0;
  let baseSoc = cfg.s0;
  const first = quarterUp(nowMs + 1);
  const [genStart, genSteps] = cfg.gen || [-1, 0];
  for (let k = 0, t = first; t <= endMs; k += 1, t += QUARTER_MS) {
    const on = k >= genStart && k < genStart + genSteps;
    soc = Math.min(100, Math.max(0, soc - cfg.draw + (on ? 3.2 : 0)));
    baseSoc = Math.max(0, baseSoc - cfg.draw);
    traj.push([isoUtc(t), round(soc, 1), on]);
    base.push([isoUtc(t), round(baseSoc, 1)]);
  }
  return { traj, base, first };
}

function runwayBattery(cfg, nowIso, nowMs) {
  const setup = runwayState.setups[cfg.sid] || null;
  const ev = runwayState.event;
  const margin = setup?.floor_margin ?? 3;
  const missing = [];
  if (!ev.load_out_at) missing.push({ key: 'load_out', label: 'load-out time' });
  if (setup?.floor_soc == null) missing.push({ key: 'floor', label: 'floor' });
  const out = {
    system_id: cfg.sid, sys_type: cfg.sysType, advised: !!setup, setup,
    recorded_low_pct: cfg.lowPct, suggested_floor_pct: cfg.lowPct + margin,
    covered: missing.length === 0, missing,
    fresh: !cfg.stale, stale_reason: cfg.stale ? 'no SOC sample for 15 minutes' : null,
    data_at: cfg.stale ? isoUtc(nowMs - 34 * 60 * 1000) : nowIso,
    forecast: null, call: null, outcome: null,
  };
  if (!out.covered) return out;
  const endMs = new Date(ev.load_out_at).getTime();
  const { traj, base, first } = runwaySteps(cfg, nowMs, endMs);
  const floor = setup.floor_soc;
  const call = cfg.gen
    ? { kind: 'start_by', start_at: isoUtc(first + (cfg.gen[0] - 1) * QUARTER_MS), start_now: false,
      run_minutes: cfg.gen[1] * 15, until: null, until_soc: null, stop_now: false, lower_to_soc: null, disarm_until: null }
    : { kind: 'no_generator', start_at: null, start_now: false, run_minutes: null,
      until: null, until_soc: null, stop_now: false, lower_to_soc: null, disarm_until: null };
  const identity = `${call.kind}|${call.start_at || ''}|${call.run_minutes || ''}`;
  if (cfg.stale) {
    out.call = {
      state: 'withdrawn', kind: call.kind, call, identity, since: isoUtc(nowMs - 3 * 3600 * 1000),
      delivery_status: 'delivered', last_sent_at: isoUtc(nowMs - 30 * 60 * 1000), updated_at: isoUtc(nowMs - 30 * 60 * 1000),
      withdrawn: { reason: 'stale', missing: [], stale_reason: out.stale_reason, data_at: out.data_at, at: isoUtc(nowMs - 18 * 60 * 1000) },
    };
    return out;
  }
  const low = traj.reduce((a, b) => (b[1] < a[1] ? b : a), traj[0]);
  const hoursLeft = Math.max(0.25, (endMs - nowMs) / 3600000);
  const kwhPerPoint = 2.15;
  const budgetKw = Math.max(0, ((cfg.s0 - floor) * kwhPerPoint) / hoursLeft);
  const pv = cfg.sid === 'cube-sto-demo';
  out.forecast = {
    computed_at: nowIso, call, identity, window_end: ev.load_out_at,
    soc_pct: cfg.s0, floor_pct: floor, min_soc_pct: low ? low[1] : cfg.s0,
    trajectory: traj, baseline: base,
    inputs: {
      floor_pct: floor, horizon: ev.load_out_at, window_end: null,
      copied_day: withWeekday(ev.show_days?.[0] || venueDay(nowMs - DAY_REAL_MS)),
      budget_based: false, assumed_draw_kw: null,
      budget_kw: round(budgetKw, 1), budget_until: ev.load_out_at,
      generator_kw: round((setup.generator_kw_override || ev.generator_kw || 0) * 0.85, 1), max_charge_kw: null,
      solar_credit: false, solar_reason: pv ? 'solar forecast not enabled' : 'no PV on this battery',
      charger: null, charger_counted: false,
      generator_running: false, generator_since: null,
    },
    copied_day: ev.show_days?.[0] || null,
    show_end: ev.show_end_override || isoUtc(quarterUp(nowMs + 2 * 3600 * 1000)),
    low_delivery: null,
  };
  out.call = {
    state: 'standing', kind: call.kind, call, identity, since: isoUtc(nowMs - 20 * 60 * 1000),
    delivery_status: 'delivered', last_sent_at: isoUtc(nowMs - 19 * 60 * 1000), updated_at: isoUtc(nowMs - 19 * 60 * 1000),
    withdrawn: null,
  };
  return out;
}

/** GET /api/runway/events/{window_id}. Only the demo site has an event
 * setup; any other window lists no batteries. */
export function computeRunway(windowId = EVENT_ID, nowMs = Date.now()) {
  const nowIso = isoUtc(nowMs);
  const own = windowId === EVENT_ID;
  return {
    window_id: windowId, event_name: own ? EVENT_NAME : windowId, ended: false,
    venue_tz: RUNWAY_TZ, venue_tz_assumed: false, now: nowIso,
    advisor: { last_run_at: isoUtc(nowMs - 2 * 60 * 1000), status: 'ok', running: true },
    solar: { credit: false, reason: 'solar forecast not enabled' },
    telemetry_error: null,
    event_setup: own ? { ...runwayState.event } : null,
    batteries: own ? RUNWAY_BATTERIES.map((cfg) => runwayBattery(cfg, nowIso, nowMs)) : [],
    proposals: own ? runwayState.proposals.map((p) => ({ ...p })) : [],
  };
}

/** The runway writes: PUT event setup, PUT battery setup, POST proposal
 * confirm or dismiss. A key present sets the field, null clears it. */
export function writeRunway(path, method, body) {
  const nowIso = isoUtc(Date.now());
  const proposal = path.match(/\/runway\/proposals\/([^/]+)\/(confirm|dismiss)$/);
  if (method === 'POST' && proposal) {
    const p = runwayState.proposals.find((x) => String(x.id) === decodeURIComponent(proposal[1]));
    if (!p) return { status: 409, body: { error: 'This proposal is already decided.', code: 'proposal_not_pending' } };
    runwayState.proposals = runwayState.proposals.filter((x) => x !== p);
    if (proposal[2] === 'dismiss') return { status: 200, body: { proposal: { ...p, status: 'dismissed' } } };
    if (p.system_id) {
      const row = { ...(runwayState.setups[p.system_id] || {}), [p.field]: p.value, updated_by: 'demo', updated_at: nowIso };
      runwayState.setups[p.system_id] = row;
      return { status: 200, body: { proposal: { ...p, status: 'confirmed' }, setup: row } };
    }
    Object.assign(runwayState.event, { [p.field]: p.value, updated_by: 'demo', updated_at: nowIso });
    return { status: 200, body: { proposal: { ...p, status: 'confirmed' }, event_setup: { ...runwayState.event } } };
  }
  const battery = path.match(/\/runway\/events\/[^/]+\/batteries\/([^/]+)\/setup$/);
  if (method === 'PUT' && battery) {
    const sid = decodeURIComponent(battery[1]);
    const stamps = {};
    for (const k of ['autostart_start_soc', 'autostart_stop_soc', 'autostart_armed']) {
      if (k in body) stamps[`${k}_at`] = nowIso;
    }
    const row = { ...(runwayState.setups[sid] || {}), ...body, ...stamps, updated_by: 'demo', updated_at: nowIso };
    runwayState.setups[sid] = row;
    return { status: 200, body: { setup: row } };
  }
  if (method === 'PUT' && /\/runway\/events\/[^/]+\/setup$/.test(path)) {
    Object.assign(runwayState.event, body, { updated_by: 'demo', updated_at: nowIso });
    return { status: 200, body: { event_setup: { ...runwayState.event } } };
  }
  return { status: 404, body: { error: 'Not found', code: 'not_found' } };
}

export const DEMO_EVENT = {
  event_id: EVENT_ID,
  event_name: EVENT_NAME,
  window_id: EVENT_ID,
  name: EVENT_NAME,
  deployment_id: DEPLOYMENT_ID,
  active: true,
};

export const HEADTRIP_EVENT = {
  event_id: HEADTRIP_EVENT_ID,
  event_name: HEADTRIP_EVENT_NAME,
  window_id: HEADTRIP_EVENT_ID,
  name: HEADTRIP_EVENT_NAME,
  deployment_id: DEPLOYMENT_ID,
  active: true,
};

/** Every event the demo serves, the demo site first. */
export const DEMO_EVENTS = [DEMO_EVENT, HEADTRIP_EVENT];

export const DEMO_CAPABILITIES = {
  context: 'cloud',
  role: 'admin',
  portal: false,
  api_key: 'demo',
  node_id: null,
  deployment_id: DEPLOYMENT_ID,
  features: { mesh_protocol: false },
  deployments: [{ deployment_id: DEPLOYMENT_ID, name: 'Demo Fleet' }],
  endpoints: {},
  demo: true,
};
